/**
 * webcodecs-frame-extractor.js — hardware-accelerated frame extraction.
 *
 *   File → MP4Box demux (streamed) → VideoDecoder (hw, when available) →
 *     OffscreenCanvas → Blob (PNG/JPEG/WebP) → caller's onFrame()
 *
 * Replaces the FFmpeg WASM `-vf fps=N` path used by video-frames for browsers
 * that support WebCodecs. Compared to the FFmpeg path:
 *   • No JS-heap copy of the source file (a 156 MB MP4 was OOMing on
 *     `new Uint8Array(await file.arrayBuffer())` — 16 events 2026-05-06/07).
 *   • Hardware decoder runs at 60-200 fps instead of WASM x264's 1-10 fps.
 *   • Memory bounded by codec state + 1 in-flight VideoFrame (~5 MB), not
 *     by input size or output count.
 *
 * What this module does NOT do (by design):
 *   • Doesn't re-encode video — frame extraction is one-way (file → images).
 *     Callers re-encode each VideoFrame to PNG/JPEG/WebP via canvas.toBlob.
 *   • Doesn't demux non-mp4 containers (mkv/avi/webm). isSupported() returns
 *     false for those; caller falls back to FFmpeg WASM (which supports any
 *     container FFmpeg can read).
 *   • Doesn't handle codecs the browser's VideoDecoder rejects. isSupported()
 *     probes via VideoDecoder.isConfigSupported({codec, description}).
 *
 * API:
 *
 *   const cap = await WebCodecsFrameExtractor.isSupported(file);
 *   // → { ok, reason, codec, width, height, fps, totalFramesEst }
 *   //   reason ∈ 'no-webcodecs' | 'no-offscreen-canvas' | 'no-mp4box' |
 *   //            'demux-failed:<msg>' | 'codec-unsupported:<codec>' |
 *   //            'no-video-track' | 'avcc-missing'
 *
 *   const result = await WebCodecsFrameExtractor.extract(file, {
 *       mode:        'all'|'fps'|'nth'|'range'|'interval'|'count'|'keyframes'|'scene',
 *       customFps:   N,                  // mode==='fps'
 *       nthFrame:    N,                  // mode==='nth'
 *       rangeStart:  seconds,            // mode==='range'
 *       rangeEnd:    seconds,            // mode==='range'
 *       intervalSec: seconds,            // mode==='interval' — one frame every N sec
 *       frameCount:  N,                  // mode==='count'    — exactly N, evenly spaced
 *       spanStart:   seconds,            // mode==='count'    — span to spread over
 *       spanEnd:     seconds,            //                     (defaults to whole file)
 *       sceneThreshold: 0..1,            // mode==='scene' — luma MAD cut level (0.14)
 *       sceneMinGapSec: seconds,         // mode==='scene' — floor between cuts (0.35)
 *       maxWidth:    px | null,          // null = original
 *       format:      'png'|'jpg'|'webp',
 *       quality:     1..100,
 *       onFrame:     async (blob, frameIndex, timestampSec) => void,
 *       onProgress:  (pct 0..1) => void,
 *       abortSignal: AbortSignal | null,
 *   });
 *   // → { framesEmitted, framesDecoded, decoderHardware, sec }
 *
 * Errors thrown by extract():
 *   • DOMException 'AbortError' on abortSignal trigger
 *   • Error 'webcodecs-fallback:<reason>' when caller should retry via FFmpeg
 *   • Plain Error for genuinely-unrecoverable conditions (decoder crash mid-stream)
 *
 * Dependencies (loaded on demand):
 *   • window.MP4Box — vendored at /vendor/mp4box/mp4box.all.min.js
 *   • Native WebCodecs (VideoDecoder, VideoFrame, EncodedVideoChunk)
 *   • Native OffscreenCanvas (Chrome 69+, Firefox 105+, Safari 16.4+)
 */
(function(){
'use strict';
if (window.WebCodecsFrameExtractor) return;

// ── MP4Box loader (mirrors mp4box-probe.js so a single load satisfies both) ──
function loadMp4Box() {
    if (window.MP4Box) return Promise.resolve();
    if (window.__mp4boxLoadingPromise) return window.__mp4boxLoadingPromise;
    window.__mp4boxLoadingPromise = new Promise(function(resolve, reject){
        var s = document.createElement('script');
        s.src = '/vendor/mp4box/mp4box.all.min.js';
        s.onload = function(){ resolve(); };
        s.onerror = function(){
            // Tier 2: same-origin jsdelivr proxy (served by Beget for RU, so it
            // works even when external CDNs are blocked). Tier 3: external CDN.
            var sources = [
                '/cdn/jsdelivr/npm/mp4box@0.5.4/dist/mp4box.all.min.js',
                'https://cdn.jsdelivr.net/npm/mp4box@0.5.4/dist/mp4box.all.min.js'
            ];
            (function tryNext(i){
                if (i >= sources.length) { reject(new Error('mp4box-load-failed')); return; }
                var s2 = document.createElement('script');
                s2.src = sources[i];
                s2.onload = function(){ resolve(); };
                s2.onerror = function(){ tryNext(i + 1); };
                document.head.appendChild(s2);
            })(0);
        };
        document.head.appendChild(s);
    });
    return window.__mp4boxLoadingPromise;
}

// ── Container detection ──
// MP4Box parses mp4 / mov / m4v / 3gp (all ISO BMFF). For .webm/.mkv/.avi/.ts
// the demux fails immediately; we short-circuit here so isSupported() returns
// the right reason without a 10-second mp4box probe timeout.
function isMp4Family(file) {
    var name = (file && file.name || '').toLowerCase();
    var type = (file && file.type || '').toLowerCase();
    if (/\.(mp4|m4v|mov|3gp|3g2)$/i.test(name)) return true;
    if (/^video\/(mp4|quicktime|3gpp|3gpp2)$/i.test(type)) return true;
    return false;
}

/* ── Decoder description, one path for every codec ──
   ⚠️ Until 2026-08-08 avcC was HAND-PACKED here (and in two other modules) while
   hvcC used the writer below. Measured against the real fixture corpus, the
   hand-packed record was wrong on 14/14 files:
     · sized `7 + 2 + …` but only 8 header bytes written → one uninitialised
       trailing byte in EVERY record;
     · High profiles (100/110/122/244) lost the trailing fields ISO/IEC 14496-15
       §5.3.3.1.2 requires (`fd f8 f8 00` = chroma 4:2:0, 8-bit, 8-bit, 0 ext).
   Baseline came out 41 B instead of 40, High 44 instead of 47. A decoder reading
   that stray `00` as `chroma_format` sees MONOCHROME; every browser we serve
   re-derives from the SPS instead, which is why it never surfaced as a bug.
   The writer below was already correct — avcC simply never used it.
   Canonical version (and a byte-exact source-slice tier for callers that hold
   the whole file): VideoMergeWebCodecs.buildDecoderDescription. This module
   feeds mp4box in 4 MB chunks, so the box offsets do not map to a local buffer
   and re-serialising is the right tier here. */
function buildConfigDescription(box) {
    // Every mp4box config box has a `write(stream)` that emits the canonical
    // DecoderConfigurationRecord including its box header. Strip the header and
    // the body IS what VideoDecoder.configure({description}) wants — no
    // per-codec field packing, nothing to keep in sync with mp4box internals.
    //
    // DataStream lives at GLOBAL scope when mp4box.all.min.js loads — NOT at
    // window.MP4Box.DataStream (which is undefined). Earlier versions of this
    // function only checked the namespaced location and silently returned
    // null, which routed every HEVC user to the FFmpeg fallback (slow + WASM
    // load) instead of the hardware VideoDecoder. Caught by manual matrix
    // test 2026-05-07 (video-hevc.mp4 → reason='avcc-missing' fallback).
    if (!box) return null;
    try {
        var DS = (window.MP4Box && window.MP4Box.DataStream) || window.DataStream;
        if (!DS || typeof box.write !== 'function') return null;
        var stream = new DS(undefined, 0, DS.BIG_ENDIAN);
        box.write(stream);
        // hdr_size, not a hardcoded 8: vpcC is a FullBox (8 + 4 version/flags)
        // and its description is the RECORD that follows those 12 bytes.
        var hdr = (box.hdr_size != null) ? box.hdr_size : 8;
        var all = new Uint8Array(stream.buffer);
        return all.length > hdr ? all.subarray(hdr) : null;
    } catch (_) { return null; }
}

// ── Phase 1: PROBE ──
// Reads moov atom (head, then tail if needed) to extract codec + dimensions
// + avcC. Returns codec config the caller can pass to VideoDecoder.configure.
// Bounded read: ≤32 MB head + ≤16 MB tail + 256 MB hard cap.
async function probeFile(file) {
    await loadOrientation();
    await loadMp4Box();
    return await new Promise(function(resolve, reject){
        var inFile = window.MP4Box.createFile();
        var settled = false;
        var to = setTimeout(function(){
            if (settled) return;
            settled = true;
            try { inFile.flush(); } catch(_){}
            reject(new Error('probe-timeout'));
        }, 15000);

        inFile.onError = function(e){
            if (settled) return;
            settled = true;
            clearTimeout(to);
            reject(new Error('demux-failed:' + (e || 'unknown')));
        };
        inFile.onReady = function(info) {
            if (settled) return;
            settled = true;
            clearTimeout(to);
            try {
                var v = (info.tracks || []).find(function(t){ return t.type === 'video'; });
                if (!v) return reject(new Error('no-video-track'));
                var trak = inFile.getTrackById(v.id);
                var entry = trak.mdia.minf.stbl.stsd.entries[0];
                var codec = v.codec;
                var description = null;
                description = buildConfigDescription(entry.avcC || entry.hvcC || entry.vpcC || entry.av1C);
                // ETALON-G: Fragmented MP4 duration recovery. Android
                // MediaMuxer / DASH segments / screen-recorder outputs often
                // report mvhd.duration=0 (no monolithic moov) but DO have
                // mvex.mehd.fragment_duration. Without this fallback the
                // probe returned durationSec=0 → emitProgress fell to the
                // time-stub branch even when honest progress was knowable.
                var dur = (info.timescale && info.duration) ? info.duration / info.timescale : 0;
                if (dur <= 0 && info.fragment_duration && info.timescale) {
                    dur = info.fragment_duration / info.timescale;
                }
                /* `width`/`height` НАРУЖУ — display-размер (после матрицы tkhd):
                   именно им меряется холст и именно его ждёт вызывающий, у
                   которого рядом живёт <video>-лейн с теми же числами.
                   Coded-размер остаётся отдельным полем — он нужен ровно
                   одному потребителю, VideoDecoder.configure(). */
                var _vo = window.VideoOrientation;
                var _cw = v.video ? v.video.width : v.track_width || 0;
                var _ch = v.video ? v.video.height : v.track_height || 0;
                var _rot = _vo ? _vo.fromTkhd(inFile, v.id) : 0;
                var _disp = _vo ? _vo.display(_cw, _ch, _rot) : { w: _cw, h: _ch };
                resolve({
                    codec: codec,
                    rotationDeg: _rot,
                    codedWidth: _cw,
                    codedHeight: _ch,
                    width: _disp.w,
                    height: _disp.h,
                    durationSec: dur,
                    timescale: v.timescale,
                    nbSamples: v.nb_samples || 0,
                    isFragmented: !!info.isFragmented,
                    description: description,
                    trackId: v.id,
                    needsDescription: /^(avc1|hev1|hvc1)/i.test(codec)
                });
            } catch (e) { reject(new Error('probe-extract-error:' + e.message)); }
        };

        // Head-then-tail feed (handles both faststart and moov-at-end mp4).
        var CHUNK = 4 * 1024 * 1024;
        var HEAD_CAP = 32 * 1024 * 1024;
        var TAIL_BYTES = 16 * 1024 * 1024;
        (async function feed(){
            try {
                var off = 0;
                while (!settled && off < file.size && off < HEAD_CAP) {
                    var end = Math.min(off + CHUNK, file.size);
                    var buf = await file.slice(off, end).arrayBuffer();
                    buf.fileStart = off;
                    var next = inFile.appendBuffer(buf);
                    if (settled) return;
                    off = (typeof next === 'number' && next > end) ? next : end;
                }
                if (!settled && file.size > HEAD_CAP) {
                    var tailStart = Math.max(off, file.size - TAIL_BYTES);
                    var tOff = tailStart;
                    while (!settled && tOff < file.size) {
                        var tEnd = Math.min(tOff + CHUNK, file.size);
                        var tBuf = await file.slice(tOff, tEnd).arrayBuffer();
                        tBuf.fileStart = tOff;
                        inFile.appendBuffer(tBuf);
                        tOff = tEnd;
                    }
                }
                if (!settled) {
                    try { inFile.flush(); } catch(_){}
                    if (!settled) {
                        settled = true;
                        clearTimeout(to);
                        reject(new Error('no-moov-found'));
                    }
                }
            } catch (e) {
                if (settled) return;
                settled = true;
                clearTimeout(to);
                reject(e);
            }
        })();
    });
}

// ── Public API ──

/* Ориентация — такая же зависимость лейна, как mp4box, поэтому модуль забирает
   её сам: блейд, забывший подключить скрипт, не должен превращаться в молча
   повёрнутые кадры у пользователя. */
function loadOrientation() {
    if (window.VideoOrientation) return Promise.resolve();
    if (window.__voLoadingPromise) return window.__voLoadingPromise;
    window.__voLoadingPromise = new Promise(function (resolve) {
        var s = document.createElement('script');
        /* Версионированный адрес: nginx отдаёт /js/… immutable на 180 дней,
           и без версии вернувшийся посетитель никогда не получит правку. */
        s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/video-orientation.js') : '/js/video-orientation.js';
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); };   // fail-open
        document.head.appendChild(s);
    });
    return window.__voLoadingPromise;
}

/* Дверь HDR → SDR (hdr-to-sdr.js): VideoOrientation.draw ведёт через неё каждый HLG/PQ-кадр — иначе в кадр
   уходит тусклая 2D-картинка браузера (docs/hdr-to-sdr.md). Общий промис с webcodecs-pipeline.js; не загрузилась —
   кадры как раньше (fail-open). */
function ensureHdrToSdr() {
    if (window.HdrToSdr) return Promise.resolve();
    if (!window.__hdrToSdrLoading) {
        window.__hdrToSdrLoading = new Promise(function (resolve) {
            var s = document.createElement('script');
            s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/hdr-to-sdr.js') : '/js/hdr-to-sdr.js';
            s.onload = function () { resolve(); };
            s.onerror = function () { window.__hdrToSdrLoading = null; resolve(); };
            document.head.appendChild(s);
        });
    }
    return window.__hdrToSdrLoading;
}

async function isSupported(file) {
    var out = { ok: false, reason: '', codec: '', width: 0, height: 0, fps: 0, totalFramesEst: 0 };
    if (!file || typeof file.slice !== 'function') {
        out.reason = 'no-file'; return out;
    }
    if (typeof VideoDecoder === 'undefined' || typeof VideoFrame === 'undefined' ||
        typeof EncodedVideoChunk === 'undefined') {
        out.reason = 'no-webcodecs'; return out;
    }
    if (typeof OffscreenCanvas === 'undefined') {
        out.reason = 'no-offscreen-canvas'; return out;
    }
    if (!isMp4Family(file)) {
        // We could still attempt mp4box (it parses some .mov variants regardless
        // of extension), but for non-mp4 containers it'd just timeout. Short-
        // circuit so the caller gets an actionable reason in <10ms.
        out.reason = 'container-unsupported'; return out;
    }
    try { await loadOrientation(); await loadMp4Box(); }
    catch (e) { out.reason = 'no-mp4box'; return out; }

    var meta;
    try { meta = await probeFile(file); }
    catch (e) {
        out.reason = e.message || 'probe-failed';
        return out;
    }
    out.codec = meta.codec;
    out.width = meta.width;
    out.height = meta.height;
    out.fps = meta.durationSec > 0 ? Math.round(meta.nbSamples / meta.durationSec) : 0;
    out.totalFramesEst = meta.nbSamples;

    if (meta.needsDescription && !meta.description) {
        out.reason = 'avcc-missing';
        return out;
    }

    // Ask the decoder if it actually supports this codec config.
    try {
        var cfg = { codec: meta.codec, codedWidth: meta.codedWidth || meta.width, codedHeight: meta.codedHeight || meta.height };
        if (meta.description) cfg.description = meta.description;
        var probe = await VideoDecoder.isConfigSupported(cfg);
        if (!probe.supported) {
            out.reason = 'codec-unsupported:' + meta.codec;
            return out;
        }
    } catch (e) {
        out.reason = 'config-probe-error:' + (e.message || '');
        return out;
    }

    out.ok = true;
    out._meta = meta;       // internal; passed to extract() so it doesn't re-probe
    return out;
}

// Build the should-emit predicate for a given extraction mode. Computed once
// per extract() call; called per decoded frame.
function buildEmitFilter(opts, fpsHint) {
    var mode = opts.mode || 'all';
    if (mode === 'all') {
        return function(){ return true; };
    }
    if (mode === 'nth') {
        var nth = Math.max(1, +opts.nthFrame || 1);
        var i = -1;
        return function(){ i++; return (i % nth) === 0; };
    }
    if (mode === 'fps') {
        // Emit one frame per (1/customFps) seconds of source time.
        var period = 1.0 / Math.max(0.5, +opts.customFps || 1);
        var nextEmitAt = 0;
        return function(_idx, tsSec){
            if (tsSec >= nextEmitAt - 1e-6) {
                nextEmitAt = tsSec + period;
                return true;
            }
            return false;
        };
    }
    if (mode === 'range') {
        var start = +opts.rangeStart || 0;
        var end = +opts.rangeEnd || Number.POSITIVE_INFINITY;
        return function(_idx, tsSec){ return tsSec >= start && tsSec <= end; };
    }
    // 'interval' is 'fps' inverted, and it exists because the two are NOT the
    // same control in a user's head. `fps` answers "how dense", and its useful
    // range bottoms out at 1 fps — so "one frame every 10 seconds", the single
    // most common ask for a long recording, was unreachable through it
    // (customFps would have to be 0.1, and the clamp above floors at 0.5).
    if (mode === 'interval') {
        var period2 = Math.max(0.02, +opts.intervalSec || 1);
        var nextAt = 0;
        return function(_idx, tsSec){
            if (tsSec >= nextAt - 1e-6) { nextAt = tsSec + period2; return true; }
            return false;
        };
    }
    // 'count' — exactly N frames, evenly spread over the span. Targets are the
    // MIDPOINTS of N equal slices, not the edges: a video's first frame is very
    // often a black or logo frame, and the last target computed on edges lands
    // on `duration`, which no sample ever reaches (the final frame starts
    // before it). Midpoints give N usable, representative frames.
    if (mode === 'count') {
        var n = Math.max(1, Math.round(+opts.frameCount || 1));
        var s0 = Math.max(0, +opts.spanStart || 0);
        var s1 = +opts.spanEnd || 0;
        if (!(s1 > s0)) {
            // Unknown duration — degrade to "every frame" rather than emitting
            // nothing. Caller still gets a result; count just isn't honoured.
            return function(){ return true; };
        }
        var step = (s1 - s0) / n;
        var targets = [];
        for (var t = 0; t < n; t++) targets.push(s0 + step * (t + 0.5));
        var ti = 0;
        return function(_idx, tsSec){
            if (ti >= targets.length) return false;
            if (tsSec < targets[ti] - 1e-6) return false;
            // Skip past every target this frame already covers — a low-fps
            // source asked for more frames than it has must not emit the same
            // frame twice.
            while (ti < targets.length && tsSec >= targets[ti] - 1e-6) ti++;
            return true;
        };
    }
    // 'keyframes' — only intra-coded (sync) samples. The set is filled by the
    // demuxer as samples arrive, so this closure reads a live object rather
    // than a snapshot; extract() hands the same Set to both sides.
    if (mode === 'keyframes') {
        var keySet = opts.__keySet || null;
        return function(_idx, _tsSec, tsMicro){
            if (!keySet) return true;
            return keySet.has(Math.round(tsMicro));
        };
    }
    // 'scene' cannot be decided from an index or a timestamp — it needs the
    // PIXELS, which only exist after the frame is drawn. It is therefore
    // handled inline in drainOne (see sceneKeep) and this filter passes
    // everything through to get there.
    if (mode === 'scene') {
        return function(){ return true; };
    }
    return function(){ return true; };
}

// ── Scene-change scoring ────────────────────────────────────────────────────
// Frame-to-frame mean absolute luma difference on a 32×32 thumbnail.
//
// Why this shape, measured on real cuts rather than assumed:
//   • 32×32 is small enough that getImageData costs ~0.1 ms (the whole scoring
//     pass over a 10-minute clip stays under a second) and large enough that a
//     cut between two similarly-lit scenes still moves the number.
//   • Luma only. A cut usually changes brightness structure; comparing three
//     channels triples the work and adds nothing a colour-cut wouldn't also
//     show in luma.
//   • Compared against the PREVIOUS DECODED frame, not the previous KEPT one.
//     Against the kept frame, a slow pan accumulates difference until it
//     crosses the threshold and fires a "cut" that never happened.
var SCENE_TN = 32;

function makeSceneDetector(opts) {
    var cv, ctx;
    try {
        cv = new OffscreenCanvas(SCENE_TN, SCENE_TN);
        ctx = cv.getContext('2d', { willReadFrequently: true });
    } catch (_) { return null; }
    if (!ctx) return null;
    var prev = null;
    var thresh = (typeof opts.sceneThreshold === 'number' && opts.sceneThreshold > 0)
        ? opts.sceneThreshold : 0.14;
    // A dissolve or a flash crosses the threshold on several consecutive
    // frames. Without a floor on the gap, one transition yields a burst of
    // near-identical stills — which is exactly the pile the mode exists to
    // avoid. 0.35 s is below any real shot length and above any transition.
    var minGap = (typeof opts.sceneMinGapSec === 'number' && opts.sceneMinGapSec >= 0)
        ? opts.sceneMinGapSec : 0.35;
    var lastKeptTs = -1e9;
    return {
        // Returns true when this frame starts a new scene.
        test: function (srcCanvas, tsSec) {
            var n = SCENE_TN * SCENE_TN, cur = new Uint8Array(n), i, p;
            try {
                ctx.drawImage(srcCanvas, 0, 0, SCENE_TN, SCENE_TN);
                var d = ctx.getImageData(0, 0, SCENE_TN, SCENE_TN).data;
                for (i = 0, p = 0; i < n; i++, p += 4) {
                    // Integer luma (BT.601 weights ×256) — no float math per pixel.
                    cur[i] = (d[p] * 77 + d[p + 1] * 150 + d[p + 2] * 29) >> 8;
                }
            } catch (_) {
                // Readback failed (GPU eviction). Treat as "no cut" rather than
                // emitting a frame we could not judge.
                return false;
            }
            var before = prev;
            prev = cur;
            // The opening frame IS the first scene, always.
            if (!before) { lastKeptTs = tsSec; return true; }
            var sum = 0;
            for (i = 0; i < n; i++) sum += Math.abs(cur[i] - before[i]);
            var score = (sum / n) / 255;
            if (score < thresh) return false;
            if (tsSec - lastKeptTs < minGap) return false;
            lastKeptTs = tsSec;
            return true;
        },
    };
}

/* Mp4EditList (public/js/mp4-edit-list.js) — the one reader of a container's
   edit lists. Loaded on demand and shared with webcodecs-pipeline.js through
   the same promise; a page that cannot load it gets raw cts, as before. */
function ensureMp4EditList() {
    if (typeof window === 'undefined') return Promise.resolve(null);
    if (window.Mp4EditList) return Promise.resolve(window.Mp4EditList);
    if (!window.__mp4EditListLoading) {
        window.__mp4EditListLoading = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = typeof window.__jsSrc === 'function' ? window.__jsSrc('/js/mp4-edit-list.js') : '/js/mp4-edit-list.js';
            s.onload = resolve;
            s.onerror = function () { reject(new Error('load: mp4-edit-list.js')); };
            document.head.appendChild(s);
        });
    }
    return window.__mp4EditListLoading.then(function () { return window.Mp4EditList || null; });
}

async function extract(file, opts) {
    opts = opts || {};
    var onFrame = typeof opts.onFrame === 'function' ? opts.onFrame : function(){};
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function(){};
    // ── Edit lists: presentation time, not composition time ─────────
    // mp4box hands out `cts` in MEDIA time. An MP4 with B-frames (x264 with
    // its default lookahead, most phone cameras) carries an `elst` that says
    // "presentation starts at media time 2 frames": every frame's timestamp
    // is 2 frames higher than where the player shows it. This extractor
    // labelled frames with raw cts, and every consumer that resamples by
    // time — frame-source.js under /remove-object-from-video and
    // /video-bg-remover, the storyboard, video-frames — emitted output frame
    // k with the SOURCE frame two behind it: the first three output frames
    // were all frame 0, the last two source frames never appeared, and the
    // original audio track, remuxed back, ran 80 ms ahead of the picture on
    // every export (measured 17.09.2026 with a burned-in frame counter:
    // Δ = −2 frames at k = 1, 2, 5, 30, 60, 120, 210, 239). Same root as
    // #14.09 (IMG_2465.mov, «звук опережает видео»), fixed 15.09 in
    // webcodecs-pipeline.js and left here because this module carried its
    // own reader. The plan below is the pipeline's; reader missing → raw
    // cts, the pre-fix behaviour, and the run says so.
    var editList = null;
    try { editList = await ensureMp4EditList(); } catch (_) { editList = null; }
    try { await ensureHdrToSdr(); } catch (_) {}
    var editShiftUs = 0, editLeadUs = 0, editEndUs = Number.POSITIVE_INFINITY, editPlan = null, hiddenTs = new Set(), editHead = 0, editTail = 0;
    var leadSkipped = 0;   // leading pictures of the cold-start keyframe left out of the decoder (see coldKey below)
    // Background-safe yield. setTimeout(0) is throttled in background tabs
    // (>=1 s, >=60 s after 5 min of "intensive throttling") — a mid-render tab
    // switch froze the decode pump and then tripped the stall deadlines as if
    // the decoder died (user report 2026-07-17: 10-min clip, tab switched,
    // "Обработка зависла"). MessageChannel macrotasks are exempt from timer
    // throttling: same event-loop yield, keeps decoding at full speed in a
    // background tab.
    var _bgyQ = [], _bgyCh = null;
    function bgYield() {
        return new Promise(function (r) {
            if (!_bgyCh) {
                try {
                    _bgyCh = new MessageChannel();
                    _bgyCh.port1.onmessage = function () { var f = _bgyQ.shift(); if (f) f(); };
                } catch (_) { setTimeout(r, 0); return; }
            }
            _bgyQ.push(r);
            _bgyCh.port2.postMessage(0);
        });
    }

    var abort = opts.abortSignal || null;

    function checkAbort() {
        if (abort && abort.aborted) throw new DOMException('cancelled', 'AbortError');
    }

    // Skip re-probe when isSupported() already gave us _meta.
    var meta = opts._meta;
    if (!meta) {
        var cap = await isSupported(file);
        if (!cap.ok) throw new Error('webcodecs-fallback:' + cap.reason);
        meta = cap._meta;
    }

    var format = opts.format || 'png';
    /* format:'bitmap' — hand the caller an ImageBitmap instead of an encoded
       file. For pipelines that only want PIXELS (video-bg-remover and
       remove-text feed frames straight back into a canvas) the PNG round trip
       is pure waste: encode a lossless 1280x720 image, then immediately decode
       it again. convertToBlob is the slowest step in this loop by a wide
       margin, and transferToImageBitmap hands over the same backing store with
       no copy at all. Encoded formats keep behaving exactly as before. */
    var asBitmap = format === 'bitmap';
    var mime = format === 'jpg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : 'image/png';
    var quality = (format === 'png' || asBitmap) ? undefined : Math.max(0.01, Math.min(1, (+opts.quality || 85) / 100));

    var maxWidth = opts.maxWidth || null;
    var outW = meta.width, outH = meta.height;
    if (maxWidth && maxWidth < outW) {
        // Even pixel — matches FFmpeg's `-vf scale=min(iw,maxW):-2`
        outW = Math.floor(maxWidth / 2) * 2;
        outH = Math.floor(meta.height * outW / meta.width / 2) * 2;
    }
    if (outW < 2) outW = 2; if (outH < 2) outH = 2;

    // ── Stall watchdog deadlines ──────────────────────────────────────
    // The Yandex Browser / Android Kirin SoC silently wedges the H.264
    // decoder on Constrained Baseline + L4.0 (codec avc1.42c028) — every
    // stock-camera mp4 from a Huawei tablet. isConfigSupported returns
    // true, configure() succeeds, decode() returns without throwing,
    // and the decoder.output callback NEVER fires. Without these
    // deadlines, pumpDecoder()'s inner backpressure loop spins forever
    // and the user sees 5 % progress for 30+ seconds before giving up.
    // (bug-report sid c2742d9c-... 2026-06-09 "Загружается только до 5%")
    //
    // FIRST_FRAME: 8 s. Healthy first-frame latency is sub-1 s; 8× safety.
    // MID_STREAM: 10 s — tighter than enhance-video's 30 s because frame
    //             extraction is more frame-paced than encoding.
    // FLUSH:      15 s wrap around the final decoder.flush() promise.
    var FIRST_FRAME_DEADLINE_MS = 8000;
    var MID_STREAM_DEADLINE_MS  = 10000;
    var FLUSH_DEADLINE_MS       = 15000;
    // A STARVED decoder is not a stalled one. Mid-stream silence counts only
    // while the decoder holds work: chunks in its queue, or more frames inside
    // it than any reorder window (REORDER_MAX). With the queue empty and the
    // reorder window the only thing unemitted, the silence is the FEEDER's —
    // `await onFrame` held by a per-frame model on a phone or a batch written
    // to OPFS — and the frames come out the moment feeding resumes. Measured
    // (prod RU, 10–17.09.2026): 851 of ~930 mid-stream stalls had queue_size=0,
    // fed − decoded within a reorder window; each one dropped a fast lane to
    // the <video>-seek fallback on remove-text / video-bg-remover /
    // remove-object / video-frames — 520 a week, desktop and phones alike.
    var REORDER_MAX = 32;
    var CONSUMER_SLOW_MS = MID_STREAM_DEADLINE_MS;

    // ── Hint cascade ──────────────────────────────────────────────────
    // Hardware first on EVERY platform. The order is owned by
    // decoder-verify.js (DecoderVerify.HINTS) where that module is on the
    // page; the constant below is the same ladder for pages without it.
    //
    // Until 2026-09-17 Android had its own order — ['prefer-software',
    // 'prefer-hardware', null] — copied from the ENCODER, where MediaCodec
    // really is unreliable, on the guess that the software decoder would be
    // "a cheap safety margin". Chromium on Android ships NO software H.264
    // decoder: `isConfigSupported` still says yes (the field is a hint), and
    // the refusal arrives asynchronously as «Unsupported configuration. Check
    // isConfigSupported() prior to calling configure()». Measured on prod (RU,
    // 30 d to 2026-09-17, phones, this module, every consumer):
    //
    //     first hint 'prefer-software':  3591 decoder errors / 2108 sessions
    //                                      882 first frames   /  515 sessions
    //     Android second rung, hardware: 1837 sessions succeeded, 10 stalled
    //                                    on the first frame
    //     mid-stream stalls:             software 151 of 515 sessions (29 %),
    //                                    hardware 451 of 2288 (20 %)
    //
    // So software-first failed FOUR runs in FIVE, cost every Android session a
    // wasted attempt, and where it did run it was 2–5× slower (57 s vs 12–24 s
    // per extraction). The same root was found and fixed twice before —
    // webcodecs-pipeline.js (2026-07-10) and reverse-webcodecs (2026-09-08,
    // decoder-verify.js) — and stayed broken here because this module carried
    // its own copy of the ladder. In range mode the wrong first hint was not
    // even a retry: the async error surfaced as `decoder-error:` (not retry-
    // eligible, see the orchestrator below), so /remove-object-from-video's
    // render fell to <video> seeking on every Android phone and sat there
    // (support ticket 16.09.2026, refund #32).
    //
    // `null`-first was an etalon-review BAND_AID call: it tags ~90 % of
    // sessions as hint='default' and lets a HW-rejecting config that succeeds
    // under null then mid-stream-stalls escape the cascade. Explicit
    // prefer-hardware throws synchronously at configure on driver-blacklisted
    // codecs (HEVC variants on Linux, 10-bit on some Windows) → cascade
    // advances to prefer-software in-process. null is the last-resort fallback
    // for impls that need no hint at all (rare).
    var HW_HINTS = (typeof window !== 'undefined' && window.DecoderVerify && Array.isArray(window.DecoderVerify.HINTS) && window.DecoderVerify.HINTS.length)
        ? window.DecoderVerify.HINTS.slice()
        : ['prefer-hardware', 'prefer-software', null];
    // One spelling for "the decoder's error callback fired", wherever the pump
    // notices it. The `:decoded=N` tail is what the cascade reads to decide
    // whether rotating to the next hint is safe (N = 0 → nothing emitted yet).
    // Callers that route on the message keep matching: 'decoder-error' stays
    // in it (video-frames.blade.php, storyboard/extract.js).
    function decoderErrorFor(err, decoded) {
        return new Error('decoder-error:' + ((err && err.message) || '').substring(0, 120) + ':decoded=' + (decoded | 0));
    }

    // ── Render canvas (single, reused across hint retries) ────────────
    var canvas = new OffscreenCanvas(outW, outH);
    var ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('webcodecs-fallback:no-canvas-ctx');
    }

    // Estimated total samples for progress; updated as we discover the real
    // count. Range mode predicts emitted-count separately for caller progress.
    var fpsHint = meta.durationSec > 0 ? meta.nbSamples / meta.durationSec : 30;
    // Sync-sample timestamps (µs), filled by the demuxer as samples arrive and
    // read by the 'keyframes' emit filter. Lives out here because the hint
    // cascade may run several attempts, each with a fresh demuxer, while the
    // filter closure is built once — so the SET must outlive the attempt. Re-
    // filling it on a retry is idempotent: same file, same sample timestamps.
    var keyTsSet = new Set();
    var keyframesOnly = (opts.mode === 'keyframes');
    if (keyframesOnly) opts.__keySet = keyTsSet;
    var emitFilter = buildEmitFilter(opts, fpsHint);
    // Built once per extract() so its `prev` thumbnail survives the whole run.
    var sceneDet = (opts.mode === 'scene') ? makeSceneDetector(opts) : null;
    if (opts.mode === 'scene' && !sceneDet) {
        // Without a scratch canvas there is no way to compare pictures, and the
        // emit filter for this mode passes everything — so carrying on would
        // hand back EVERY frame of the video under the label "one per scene".
        // Hand the job to the FFmpeg lane, which detects scenes on its own,
        // rather than returning a confidently wrong pile.
        throw new Error('webcodecs-fallback:no-scene-canvas');
    }

    var slugTag = (opts && opts.slug) || 'video-frames';
    var t0 = performance.now();

    // ── Hint cascade orchestrator ─────────────────────────────────────
    // Try each hint in turn. If the attempt fires the first-frame deadline
    // (no frames emitted yet to the caller — fully safe to retry without
    // duplicates), move to the next hint. Mid-stream stalls AND flush
    // stalls bypass the cascade: the caller (video-frames.blade.php) sees
    // a `webcodecs-fallback:` prefix and routes to FFmpeg WORKERFS where
    // it resets ext.frames/$('vfGrid') before re-extracting (so no dupes).
    var lastErr = null;
    var attemptsTried = 0;
    // Callers may pin the cascade. The one caller that does is the FFmpeg
    // fallback in colorize-video-pipeline: by the time it runs, the HARDWARE
    // decoder has already stalled on this clip, and a wedged HW decoder cannot
    // be reclaimed from JS — retrying hardware on the freshly transcoded temp
    // walked straight back into it. Software decode of a short-GOP proc-sized
    // temp is cheap and structurally cannot hit the same wedge.
    var hints = (opts && Array.isArray(opts.hints) && opts.hints.length) ? opts.hints : HW_HINTS;
    for (var hi = 0; hi < hints.length; hi++) {
        attemptsTried++;
        try {
            var result = await runAttempt(hints[hi], hi);
            // ── Terminal telemetry (etalon-B) ───────────────────────────
            // Single source of truth for "extraction completed AND which
            // hint won". Pre-fix there was no event tagged with the
            // surviving hint — we literally could not answer "of Yandex
            // Android users who hit cascade, what % succeed on hint 0
            // vs 1 vs 2". This event closes that visibility gap and
            // becomes the watch metric for HW_HINTS drift when Chromium
            // updates MediaCodec.
            try {
                if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: slugTag,
                        action: 'webcodecs_extract_done',
                        stage: 'extract',
                        codec: (meta && meta.codec) || '',
                        hint: result.hint || 'default',
                        hint_idx: result.hint_idx,
                        attempts: attemptsTried,
                        ms: Math.round(performance.now() - t0),
                        frames_emitted: result.framesEmitted,
                        frames_decoded: result.framesDecoded,
                        decoder_hw: result.decoderHardware ? 1 : 0,
                        src_w: (meta && meta.width) || 0,
                        src_h: (meta && meta.height) || 0,
                        // the edit list: what it shifted and hid (0/none = a plain file)
                        edit: editList ? (editPlan ? (editPlan.shape || 'none') : 'none') : 'reader-missing',
                        edit_shift_ms: Math.round(editShiftUs / 1000),
                        edit_head: editHead, edit_tail: editTail, lead: leadSkipped
                    });
                }
            } catch (_) {}
            return result;
        } catch (e) {
            lastErr = e;
            checkAbort();   // user-cancel always wins
            var msg = (e && e.message) || '';
            // Retry-eligible: error occurred BEFORE any frame was emitted
            // to the caller. Safe to rotate to the next hint without dupes.
            //   • no-output-deadline:first-frame — watchdog fired before
            //     any decoder.output (Yandex/Kirin silent wedge class).
            //   • decoder-configure-failed — synchronous configure throw
            //     (driver-blacklisted HW codec; common for HEVC on Linux).
            //   • decoder-died:...:decoded=0 — runtime decoder.error fired
            //     before any output. This was missed in the first ship:
            //     a HW-rejecting config that succeeded at configure() then
            //     errored at first decode escaped the cascade (decoder-died
            //     didn't match first-frame regex) → blade fell straight to
            //     FFmpeg WORKERFS, ~10× slower. Now retries internally with
            //     prefer-software in-process. Per master-diagnosis amplifier B.
            //   • decode-throw:...:decoded=0 — synchronous decode() throw
            //     before any output (same retry safety profile).
            // Mid-stream or post-frame errors fall through to caller, which
            // resets ext.frames before routing to FFmpeg → no dupe risk.
            //   • decoder-error:...:decoded=0 — the SAME asynchronous refusal
            //     seen from the range-mode pump instead of isDecoderUsable():
            //     which of the two throws first is a race, and until
            //     2026-09-17 this spelling carried no count and matched
            //     nothing below, so a first-hint refusal that landed here
            //     abandoned WebCodecs outright — every Android render of
            //     /remove-object-from-video fell to <video> seeking.
            if (/no-output-deadline:first-frame/.test(msg) ||
                /decoder-configure-failed/.test(msg) ||
                /(decoder-died|decode-throw|decoder-error):.*:decoded=0$/.test(msg)) {
                continue;
            }
            // Anything else — mid-stream stall, flush stall, demux-error,
            // post-frame decoder-died/decode-throw — caller decides
            // (likely → FFmpeg WORKERFS path with fresh ext.frames state).
            throw e;
        }
    }
    // Every hint exhausted on first-frame stall — give up, signal to caller.
    var allHintsErr = new Error(
        'webcodecs-fallback:hw-stalled-all-hints:' +
        ((lastErr && lastErr.message) || 'unknown').substring(0, 160)
    );
    throw allHintsErr;

    // ────────────────────────────────────────────────────────────────────
    // Single-attempt body. Sets up its own decoder + mp4box; on stall or
    // any decoder error, throws a typed `webcodecs-fallback:*` error that
    // the orchestrator (above) decides what to do with.
    // ────────────────────────────────────────────────────────────────────
    async function runAttempt(hint, hintIdx) {
        var decodedFrames = [];
        // Woken by the output callback: the flush drain sleeps on it instead
        // of polling (see the flush block below).
        var outputWaiter = null;
        var pendingDecoderFlush = null; // tracked so we can attach orphan-catch on stall
        var decodeError = null;
        var decoderFrameCount = 0;
        var lastOutputAt = Date.now();    // bumped by output callback; gates stall check
        var samplesFed = 0;               // gates stall check (don't fire before any feed)
        var firstFrameEmitted = false;
        var attemptStart = Date.now();
        var stallTriggered = false;
        // Feeding finished → the FLUSH owns this phase, not the mid-stream
        // watchdog. See the watchdog body for why this flag has to exist.
        var feedingDone = false;

        var decoder = new VideoDecoder({
            output: function(frame){
                if (abort && abort.aborted) { try { frame.close(); } catch(_){} return; }
                if (stallTriggered)        { try { frame.close(); } catch(_){} return; }
                /* Кадр становится display-ориентированным ЗДЕСЬ — в одной
                   точке, через которую он проходит, — а не у тринадцати
                   потребителей этого модуля. */
                if (window.VideoOrientation && meta.rotationDeg) {
                    frame = window.VideoOrientation.apply(frame, meta.rotationDeg);
                }
                decoderFrameCount++;
                lastOutputAt = Date.now();
                if (!firstFrameEmitted) {
                    firstFrameEmitted = true;
                    // First-frame telemetry — surfaces codec-specific
                    // first-frame latency in /pino without needing a user
                    // bug report. Per master-diagnosis §6 P5.
                    try {
                        if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                            window.__ae('feature_use', {
                                slug: slugTag,
                                action: 'webcodecs_first_frame',
                                stage: 'extract',
                                hint: hint || 'default',
                                hint_idx: hintIdx,
                                codec: meta && meta.codec,
                                ms_since_attempt_start: Date.now() - attemptStart
                            });
                        }
                    } catch (_) {}
                }
                decodedFrames.push(frame);
                if (outputWaiter) { var wakeDrain = outputWaiter; outputWaiter = null; wakeDrain(); }
            },
            error: function(e){
                decodeError = e;
                // Telemetry mirrors webcodecs-pipeline.js's _aeWebcodecsErr —
                // pre-fix the only signal was a generic caller-side error toast,
                // root cause invisible. Now the WebCodecs DOMException type +
                // codec + decoded-so-far count surface in /pino as actionable
                // sub-classes.
                try {
                    if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                        window.__ae('feature_use', {
                            slug: slugTag,
                            action: 'webcodecs_decoder_error',
                            stage: 'extract',
                            hint: hint || 'default',
                            hint_idx: hintIdx,
                            codec: meta && meta.codec,
                            src_w: meta && meta.width,
                            src_h: meta && meta.height,
                            decoded_so_far: decoderFrameCount,
                            err_name: e && e.name,
                            err: ((e && e.message) || '').substring(0, 200)
                        });
                    }
                } catch (_) {}
            }
        });

        // Configure with this hint
        var decoderHardware = false;
        try {
            var cfg = { codec: meta.codec, codedWidth: meta.codedWidth || meta.width, codedHeight: meta.codedHeight || meta.height };
            if (meta.description) cfg.description = meta.description;
            if (hint) cfg.hardwareAcceleration = hint;
            decoder.configure(cfg);
            decoderHardware = hint === 'prefer-hardware';
        } catch (cfgErr) {
            try { decoder.close(); } catch(_){}
            // Per-hint configure failures get their own event class so the
            // hint-cascade telemetry distribution is interpretable (etalon-B).
            // Pre-fix, configure throws were silent — the outer loop just
            // moved on, no /pino signal.
            try {
                if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: slugTag,
                        action: 'webcodecs_configure_failed',
                        stage: 'extract',
                        codec: (meta && meta.codec) || '',
                        hint: hint || 'default',
                        hint_idx: hintIdx,
                        err_name: cfgErr && cfgErr.name,
                        err: ((cfgErr && cfgErr.message) || '').substring(0, 200)
                    });
                }
            } catch (_) {}
            throw new Error('webcodecs-fallback:decoder-configure-failed:hint=' +
                (hint || 'default') + ':' + ((cfgErr && cfgErr.message) || '').substring(0, 80));
        }

        // Helper used by the pump loop. The decoder transitions to 'closed' on
        // any unrecoverable internal error (no GPU support, malformed bitstream,
        // codec mismatch). Calling decode() on a closed decoder throws
        // InvalidStateError synchronously. Detect early so the caller can
        // fall back to FFmpeg cleanly.
        function isDecoderUsable(){
            if (decodeError) return false;
            try { return decoder.state === 'configured'; } catch(_) { return false; }
        }

        // Throw a typed stall error if the decoder hasn't produced output
        // within the deadline. Caller catches the typed message and either
        // retries with the next hint (first-frame) or falls to FFmpeg
        // (mid-stream / flush). On fire we ALSO emit telemetry and set
        // stallTriggered so any late `output` callback closes the frame
        // and short-circuits to avoid duplicate frame leaks.
        // Does the decoder hold work it is not returning? (see REORDER_MAX)
        function decoderHasWork(){
            return decoder.decodeQueueSize > 0 || (samplesFed - decoderFrameCount) > REORDER_MAX;
        }
        // ── …and is it the DECODER that keeps it? (2026-10-02) ──────────
        // A hardware decoder renders into a fixed pool of pictures, and a
        // VideoFrame not yet closed keeps its picture (the flush below met
        // the same thing). While the consumer works on a frame, or frames it
        // has not taken yet wait in decodedFrames, the decoder can be out of
        // pictures with a chunk in its queue — busy-looking, and stopped by
        // US. Prod RU+intl, 18.09–02.10, mid-stream stalls with queue ≥ 1 and
        // fed − decoded ≤ 12: video-bg-remover 345, remove-text 248,
        // video-frames 151, remove-object 139 — «silence» 16–100 s, i.e. a
        // model or a writer on the consumer side. Silence counts only while
        // we hold nothing, and from whichever came last: an output or the
        // consumer handing the loop back. Proved with a decoder that has a
        // fixed pool (tests/playwright/frame-extractor-stall-is-the-decoders-
        // not-the-consumers.mjs, case C).
        var consumerBusy = false;
        var consumerReturnedAt = 0;
        function heldByUs(){ return consumerBusy || decodedFrames.length > 0; }
        function silenceMs(){ return Date.now() - Math.max(lastOutputAt, consumerReturnedAt); }
        async function toConsumer(payload, idx, tsSec){
            consumerBusy = true;
            try { await onFrame(payload, idx, tsSec); }
            finally { consumerBusy = false; consumerReturnedAt = Date.now(); }
        }
        var consumerSlowReported = false;
        function noteConsumerSlow(idle){
            if (consumerSlowReported) return;
            consumerSlowReported = true;
            try {
                if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: slugTag, action: 'webcodecs_consumer_slow', stage: 'extract',
                        idle_ms: idle, decoded_so_far: decoderFrameCount, samples_fed: samplesFed,
                        queue_size: decoder.decodeQueueSize
                    });
                }
            } catch (_) {}
        }
        function checkStall(){
            if (samplesFed === 0) return;   // nothing fed yet — no stall to detect
            var idle = silenceMs();
            var kind = null;
            if (decoderFrameCount === 0 && idle > FIRST_FRAME_DEADLINE_MS) {
                kind = 'first-frame';
            } else if (decoderFrameCount > 0 && idle > MID_STREAM_DEADLINE_MS) {
                if (decoderHasWork() && !heldByUs()) kind = 'mid-stream';
                else noteConsumerSlow(idle);
            }
            if (!kind) return;
            stallTriggered = true;
            try {
                if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: slugTag,
                        action: 'webcodecs_decoder_stalled',
                        stage: 'extract',
                        hint: hint || 'default',
                        hint_idx: hintIdx,
                        codec: meta && meta.codec,
                        src_w: meta && meta.width,
                        src_h: meta && meta.height,
                        deadline_kind: kind,
                        decoded_so_far: decoderFrameCount,
                        queue_size: decoder.decodeQueueSize,
                        samples_fed: samplesFed,
                        idle_ms: idle,
                        held: decodedFrames.length
                    });
                }
            } catch (_) {}
            throw new Error('webcodecs-fallback:no-output-deadline:' + kind +
                ':hint=' + (hint || 'default') +
                ':decoded=' + decoderFrameCount +
                ':queue=' + decoder.decodeQueueSize +
                ':fed=' + samplesFed);
        }

        var frameIndex = 0;
        var emittedCount = 0;
        // Samples the demuxer dropped without decoding (keyframes-only mode).
        // Counted so the progress bar still tracks position in the FILE — a bar
        // driven by decoded frames alone would crawl to ~2 % and stop, because
        // in this mode ~98 % of the work is demux, not decode.
        var samplesSkipped = 0;

        // Progress fallback ladder — pre-fix, onProgress only fired when
        // meta.nbSamples > 0. mp4box returns nbSamples=0 for fragmented MP4s,
        // some Android-camera mp4s with non-standard stbl, and screen-recorder
        // outputs → progress bar frozen at 5 % even though extraction was
        // actually running fine.
        //
        // Ladder (etalon-F + etalon-H refinements):
        //   1. nbSamples>0     → frameIndex / (effective sample count, range-aware)
        //   2. durationSec>0   → tsSec / (effective duration, range-aware)
        //   3. last resort     → elapsedMs / expectedMs, where expectedMs scales
        //                        with file.size at ~10 MB/s (qualitatively right
        //                        for the degenerate-file cohort that hits this
        //                        branch). Clamped 15 s ≤ expected ≤ 240 s so
        //                        tiny clips and gigantic files both see motion.
        //   K: dispatches `stall-tick` for forward-compat with __withStallGuard
        //      consumers (precedent: enhance-video, ai-upscale, join blades).
        var __rangeStart = +opts.rangeStart || 0;
        var __rangeEnd   = (opts.mode === 'range' && (+opts.rangeEnd || 0) > 0) ? +opts.rangeEnd : 0;
        var __effDurSec  = (__rangeEnd > 0)
            ? Math.max(0, Math.min(meta.durationSec || Infinity, __rangeEnd) - __rangeStart)
            : (meta.durationSec || 0);
        // Effective sample count: nbSamples is whole-file, scale to the active
        // window when range mode is selected (denominator was the whole-file
        // count → bar maxed at e.g. 8 % then snapped to 100 % on cleanup).
        var __effSamples = (meta.nbSamples > 0 && (meta.durationSec || 0) > 0 && __effDurSec > 0)
            ? Math.max(1, Math.round(meta.nbSamples * (__effDurSec / meta.durationSec)))
            : meta.nbSamples;
        // Throughput-derived expectedMs for the time-stub branch: ~10 MB/s
        // matches what FFmpeg-via-WORKERFS averages on a mid-range desktop
        // when WebCodecs has fully failed (the cohort that reaches this branch).
        var __expectedMs = Math.min(240000, Math.max(15000,
            (file.size / (10 * 1024 * 1024)) * 1000));

        function emitProgress(tsSec){
            try { window.dispatchEvent(new CustomEvent('stall-tick', { detail: { slug: slugTag } })); } catch (_) {}
            if (__effSamples > 0) {
                onProgress(Math.min(0.95, (frameIndex + samplesSkipped) / __effSamples));
            } else if (__effDurSec > 0 && tsSec > 0) {
                var effTs = (opts.mode === 'range' && __rangeStart > 0) ? Math.max(0, tsSec - __rangeStart) : tsSec;
                onProgress(Math.min(0.95, effTs / __effDurSec));
            } else {
                var elapsedMs = Date.now() - attemptStart;
                onProgress(Math.min(0.9, elapsedMs / __expectedMs));
            }
        }

        async function drainOne(){
            // ETALON-I: hard guard at top of drainOne so post-stall late
            // calls don't reach convertToBlob / user onFrame. Pre-fix the
            // output callback had the stallTriggered guard but drainOne
            // didn't — frames already queued before stall could still
            // leak through and push to ext.frames in the caller.
            if (stallTriggered) {
                var fr0 = decodedFrames.shift();
                if (fr0) { try { fr0.close(); } catch(_){} }
                return false;
            }
            var fr = decodedFrames.shift();
            if (!fr) return false;
            // Abort-check #1 — drop any frame the VideoDecoder buffered before
            // cancel fired. Without this we keep rendering + calling onFrame
            // until the buffer empties, and the caller's onFrame may have torn
            // down its state already (e.g. video-frames.blade.php sets
            // ext.cx = null on cleanup, then `ext.cx.cancelled` throws).
            if (abort && abort.aborted) {
                try { fr.close(); } catch(_){}
                return false;
            }
            // a frame the edit list hides (before the cut, after the end) was
            // decoded for its references only — never shown, never counted
            if (hiddenTs.size && hiddenTs.has(fr.timestamp)) {
                try { fr.close(); } catch(_){}
                return true;
            }
            var tsSec = (fr.timestamp || 0) / 1_000_000;
            var thisIdx = frameIndex;
            frameIndex++;

            // Range mode: skip frames before start, stop after end — decided
            // BEFORE the emit filter, and that order is the whole point.
            //
            // The range emit filter answers "outside [start, end]" with false,
            // and the filter used to run first: every frame past the end was
            // closed and counted as "keep going", so 'past-range' was
            // unreachable. The extractor then decoded the REST OF THE FILE for
            // a window of a fraction of a second, and ended in the flush
            // watchdog. Measured 2026-09-13 in Chrome 152 with hardware decode:
            // 10 runs of 10 (0.84-s windows and full passes, 720p and 1080p)
            // came back as `flush-stalled` → <video>-seek resume, 15–24 s each;
            // /remove-object read 38% of its clips on that slow resume in prod.
            if (opts.mode === 'range') {
                var rs = +opts.rangeStart || 0;
                var re = +opts.rangeEnd || Number.POSITIVE_INFINITY;
                if (tsSec > re + 1e-3) {
                    try { fr.close(); } catch(_){}
                    return 'past-range';
                }
                if (tsSec < rs - 1e-3) {
                    try { fr.close(); } catch(_){}
                    return true;
                }
            }

            // 3rd arg is the raw µs timestamp — the only value that matches a
            // demuxed sample exactly. Seconds go through a float division and
            // cannot be compared for identity. Existing filters ignore it.
            var keep = emitFilter(thisIdx, tsSec, fr.timestamp || 0);
            if (!keep) {
                try { fr.close(); } catch(_){}
                return true;
            }

            try {
                if (window.VideoOrientation) window.VideoOrientation.draw(ctx, fr, 0, 0, outW, outH);
                else ctx.drawImage(fr, 0, 0, outW, outH);
            } finally {
                try { fr.close(); } catch(_){}
            }
            // Scene mode decides HERE, not in the emit filter: the verdict is a
            // property of the PICTURE, and the picture only exists after the
            // draw above. Every frame pays for the draw (cheap) and a rejected
            // one skips the encode below, which is the expensive half.
            if (sceneDet && !sceneDet.test(canvas, tsSec)) {
                emitProgress(tsSec);
                return true;
            }
            // convertToBlob can fail with "Readback of the source image has
            // failed" when the GPU process restarts mid-decode or VRAM
            // pressure from another app evicts the texture (Chrome/Opera
            // desktop, 4× events 2026-05/06). One-shot retry costs nothing
            // when it succeeds and surfaces a clean error if it doesn't.
            var blob;
            if (asBitmap) {
                // no encode, no decode — the caller draws this straight back
                var bmp = canvas.transferToImageBitmap
                    ? canvas.transferToImageBitmap()
                    : await createImageBitmap(canvas);
                if (abort && abort.aborted) { try { bmp.close(); } catch (_) { } return false; }
                await toConsumer(bmp, emittedCount, tsSec);
                emittedCount++;
                emitProgress(tsSec);
                return true;
            }
            try {
                blob = await canvas.convertToBlob({ type: mime, quality: quality });
            } catch (blobErr) {
                var bm = (blobErr && blobErr.message) || '';
                if (/Readback of the source image|read.?back/i.test(bm)) {
                    // Yield once for the GPU process to settle, then retry
                    // (bg-safe: a hidden tab clamps setTimeout to >=1 s).
                    await (typeof document !== 'undefined' && document.hidden
                        ? bgYield() : new Promise(function(r){ setTimeout(r, 50); }));
                    try {
                        blob = await canvas.convertToBlob({ type: mime, quality: quality });
                    } catch (blobErr2) {
                        throw new Error('webcodecs-fallback:convert-blob-failed:' +
                            ((blobErr2 && blobErr2.message) || '').substring(0, 80));
                    }
                } else {
                    throw blobErr;
                }
            }
            // Abort-check #2 — abort may have fired during the async render
            // (convertToBlob is the slowest step). Stop here rather than calling
            // a torn-down onFrame; caller cleanup may have run on the microtask
            // gap between drawImage and convertToBlob.
            if (abort && abort.aborted) return false;
            await toConsumer(blob, emittedCount, tsSec);
            emittedCount++;
            emitProgress(tsSec);
            return true;
        }

        // ── Streaming demux: feed file → mp4box → onSamples → decoder ──
        // Reuses the head-then-tail-then-body strategy from webcodecs-pipeline's
        // _demuxMp4 but pumps EncodedVideoChunks into the decoder *as samples
        // arrive* instead of accumulating the whole sample table. This keeps RAM
        // bounded by the in-flight sample window (~100 chunks × avg sample size)
        // not by video duration.
        var demuxFile = window.MP4Box.createFile();
        var pendingSamples = [];
        var demuxError = null;
        var demuxDone = false;
        var rangeReached = false;

        // Range mode, window far from the start: where the parser should
        // resume feeding after jumping to the keyframe before the window, and
        // how many samples that jump skipped on purpose (the completeness
        // check below must not count them as a short read).
        var pendingSeekOffset = null;
        var seekSkipped = 0;
        /* A keyframe the decoder reaches without the samples before it is a COLD
           start: the clip's first, and the one the range seek jumps to (the parser
           hands out the first batch of the clip, then the keyframe before the
           window — measured on an iPhone clip, faststart or not: sample 0 is fed
           first). That keyframe's leading pictures (after it in decode order, shown
           before it: an iPhone's HEVC CRA and its RASL) reference what was never
           decoded; WebKit turns the first of them into «Decoder failure» and closes
           the decoder. The rule has one owner, window.__leadingPictures
           (timbrica-core.js); the samples arrive here as a stream, so it is asked
           about the cold key and one sample at a time, until the next key. A jump
           is a gap in the parser's sample numbers. Counted with the deliberate skips. */
        var coldKey = null, coldOpen = false, lastNumSeen = -2; leadSkipped = 0;
        function leadingOfColdKey(ts) {
            return typeof window.__leadingPictures === 'function' &&
                window.__leadingPictures(2, 0, function (j) { return j ? ts : coldKey.ts; }, function (j) { return j === 0; }).length > 0;
        }
        demuxFile.onError = function(e){ demuxError = new Error('demux:' + e); };
        demuxFile.onReady = function(info){
            var v = (info.tracks || []).find(function(t){ return t.type === 'video'; });
            if (!v) { demuxError = new Error('demux:no-video-track'); return; }
            if (editList) {
                try {
                    editPlan = editList.plan(v, info.timescale);
                    if (editPlan && editPlan.mediaStartUs !== null && editPlan.mediaStartUs !== undefined) {
                        editLeadUs = editPlan.leadUs > 0 ? editPlan.leadUs : 0;
                        editShiftUs = editLeadUs - editPlan.mediaStartUs;
                        editEndUs = editPlan.presDurUs > 0 ? editLeadUs + editPlan.presDurUs : Number.POSITIVE_INFINITY;
                    }
                } catch (_) { editPlan = null; editShiftUs = 0; editLeadUs = 0; editEndUs = Number.POSITIVE_INFINITY; }
            }
            // 50 samples per batch — enough to keep the decoder fed without
            // exploding queue size on multi-minute videos.
            demuxFile.setExtractionOptions(v.id, null, { rapAlignement: true, nbSamples: 50 });
            /* A window deep into the clip starts at the keyframe before it,
               not at byte 0. Without this a 0.8-s window at 0:40 of a phone
               clip decoded forty seconds of video to throw away — the reason a
               coverage probe on <video> seeks was once the faster route.
               Sample tables must be complete for a seek, so fragmented MP4 keeps
               the linear read. A failed seek changes nothing: feeding simply
               continues from where the parser is. */
            var rsSeek = +opts.rangeStart || 0;
            if (opts.mode === 'range' && rsSeek > 0.5 && !info.isFragmented && typeof demuxFile.seek === 'function') {
                try {
                    // the window is in PRESENTATION seconds; mp4box seeks media time
                    var sk = demuxFile.seek(Math.max(0, rsSeek - editShiftUs / 1_000_000), true);
                    var trk = typeof demuxFile.getTrackById === 'function' ? demuxFile.getTrackById(v.id) : null;
                    if (sk && isFinite(sk.offset) && sk.offset > 0 && trk && trk.nextSample > 0) {
                        pendingSeekOffset = sk.offset;
                        seekSkipped = trk.nextSample;
                    }
                } catch (_) { pendingSeekOffset = null; seekSkipped = 0; }
            }
            /* start() AFTER the seek (2026-11-04). start() hands out at once every
               sample whose bytes the parser already holds — with the moov at the end
               (every iPhone .MOV, ffmpeg without faststart) that is the whole first
               read, 4 MB. Started first, the window's frames came twice: the early
               batch reached into the window, the seek rewound to the keyframe before
               it and the same samples came again (a 4.7 MB H.264 clip, window
               10.2–11 s: 36 frames for 25, 10.2…10.5 s twice), and every window
               decoded that first batch to throw away. Seeked first, the parser
               starts at the window's keyframe. */
            demuxFile.start();
        };
        demuxFile.onSamples = function(track_id, _user, samples){
            var lastNum = 0;
            for (var i = 0; i < samples.length; i++) {
                var s = samples[i];
                // Track the batch high-water mark BEFORE any skip path: the
                // releaseUsedSamples() call below is what stops mp4box from
                // accumulating unbounded buffers, and a `continue` that jumps
                // over it silently caps the demuxer at ~2 batches on a long
                // video (the failure this loop's own comment warns about).
                if (s.number > lastNum) lastNum = s.number;
                // mp4box gives cts in track-timescale units; convert to microseconds
                // for WebCodecs.
                var ts = s.cts * 1_000_000 / meta.timescale + editShiftUs;
                var dur = s.duration * 1_000_000 / meta.timescale;
                var jumped = s.number !== lastNumSeen + 1;
                lastNumSeen = s.number;
                if (s.is_sync) { coldOpen = jumped; coldKey = jumped ? { ts: ts } : null; }
                else if (jumped) coldOpen = false;            // no keyframe to start from: nothing to judge
                else if (coldOpen && leadingOfColdKey(ts)) { samplesSkipped++; leadSkipped++; continue; }
                if (editPlan) {
                    // hidden head/tail frames are still DECODED (references) —
                    // the drain drops their output by timestamp (hiddenTs);
                    // the frame straddling the cut is clamped to it, as
                    // Mp4EditList.applyVideo does for the pipeline
                    if (dur > 0 && ts + dur <= editLeadUs + 1) { hiddenTs.add(Math.round(ts)); editHead++; }
                    else if (ts >= editEndUs) { hiddenTs.add(Math.round(ts)); editTail++; }
                    else if (ts < editLeadUs) { dur = dur > 0 ? Math.max(1, dur - (editLeadUs - ts)) : dur; ts = editLeadUs; }
                }
                // Keyframes-only: a sync sample is intra-coded, so it decodes
                // standalone — the delta samples between them are pure cost
                // with no output. Dropping them here (not at emit time) is what
                // makes the mode fast rather than merely selective: a typical
                // 2-4 s GOP means ~1 sample in 60 reaches the decoder.
                if (keyframesOnly) {
                    if (!s.is_sync) { samplesSkipped++; continue; }
                    keyTsSet.add(Math.round(ts));
                }
                pendingSamples.push({
                    data: s.data,
                    timestamp: Math.round(ts),
                    duration: Math.round(dur),
                    is_sync: !!s.is_sync
                });
            }
            // Crucial: release samples mp4box still holds. Without this it
            // accumulates unbounded buffers and stops emitting after ~2 batches
            // on long videos (observed in webcodecs-pipeline).
            try { demuxFile.releaseUsedSamples(track_id, lastNum); } catch(_){}
        };

        // Helper: drain pendingSamples into decoder with backpressure.
        async function pumpDecoder(){
            while (pendingSamples.length) {
                checkAbort();
                checkStall();
                // Backpressure: cap in-flight frames so the GPU buffer pool
                // doesn't OOM on long videos. ~30 in-flight is safe everywhere.
                while (decoder.decodeQueueSize > 30 || decodedFrames.length > 20) {
                    await bgYield();
                    while (true) {
                        var step = await drainOne();
                        if (step === 'past-range') {
                            rangeReached = true;
                            break;
                        }
                        if (!step) break;
                    }
                    if (rangeReached) return;
                    if (decodeError) throw decoderErrorFor(decodeError, decoderFrameCount);
                    checkAbort();
                    // CRITICAL: this stall check is what closes the user's bug.
                    // Pre-fix, when decoder.output never fires (Yandex/Kirin
                    // Android wedge class), this inner loop spun forever —
                    // decodedFrames stays empty, queue stays > 30, no error
                    // callback. Watchdog now throws a typed fallback after
                    // FIRST_FRAME_DEADLINE_MS / MID_STREAM_DEADLINE_MS.
                    checkStall();
                }
                if (!isDecoderUsable()) {
                    // Include decoded-so-far count so the cascade orchestrator
                    // can decide retry safety (decoded=0 → safe to rotate
                    // to next hint without dupes; decoded>0 → caller has
                    // frames already, fall to FFmpeg with fresh state).
                    throw new Error('webcodecs-fallback:decoder-died' +
                        (decodeError ? ':' + (decodeError.message||'').substring(0, 80) : '') +
                        ':decoded=' + decoderFrameCount);
                }
                var s = pendingSamples.shift();
                try {
                    decoder.decode(new EncodedVideoChunk({
                        type: s.is_sync ? 'key' : 'delta',
                        timestamp: s.timestamp,
                        duration: s.duration,
                        data: s.data
                    }));
                    samplesFed++;
                } catch (decErr) {
                    // InvalidStateError (decoder closed mid-stream) or
                    // DataError (malformed sample). Tagged with decoded-so-far
                    // so cascade can retry safely when no frames emitted yet.
                    throw new Error('webcodecs-fallback:decode-throw:' +
                        (decErr.message||'').substring(0, 80) +
                        ':decoded=' + decoderFrameCount);
                }
                // Yield to let decoder.output fire, then drain
                await bgYield();
                while (true) {
                    var step2 = await drainOne();
                    if (step2 === 'past-range') {
                        rangeReached = true;
                        break;
                    }
                    if (!step2) break;
                }
                if (rangeReached) return;
                if (decodeError) throw decoderErrorFor(decodeError, decoderFrameCount);
            }
        }

        // No HEAD_CAP / TAIL_BYTES here on purpose: the feed loop below asks
        // the parser where to read instead of guessing where the moov lives.
        // Those two constants WERE the bug — see the loop's comment.
        var CHUNK = 4 * 1024 * 1024;
        var infoReady = false;
        demuxFile.onReady = (function(orig){
            return function(info){ infoReady = true; orig(info); };
        })(demuxFile.onReady);

        // Wrapped slice-read that converts the browser's cryptic file-handle-
        // revocation errors into a typed FILE_LOST DOMException via the shared
        // ToolErrors helper (etalon-D — was a 4th diverged in-module copy of
        // the same detection logic; now one canonical detect+construct pair).
        // mp4box-trim.js, webcodecs-pipeline.js, ffmpeg-processor.js will be
        // migrated in follow-ups to use the same helper.
        async function _slice(off, end){
            try {
                return await file.slice(off, end).arrayBuffer();
            } catch (err) {
                if (window.ToolErrors && window.ToolErrors.isFileLostError &&
                    window.ToolErrors.isFileLostError(err)) {
                    throw window.ToolErrors.fileLostError(err && err.message);
                }
                throw err;
            }
        }

        // ── Interval-driven stall watchdog (etalon-E) ─────────────────────
        // Belt-and-suspenders companion to the polled checkStall() calls in
        // pumpDecoder + scan loops. The polled approach requires explicit
        // invocation at every yield point; if a future maintainer adds a new
        // await (e.g. canvas.convertToBlob, await onFrame, _slice) without
        // remembering checkStall(), the wedge re-appears. The interval runs
        // INDEPENDENTLY — it cannot be bypassed by a missed callsite.
        // Pattern source: enhance-video.blade.php:4738-4769 (HW stall
        // watchdog around WebCodecsPipeline.run), validated for the
        // identical Yandex/Android wedge class. Pre-attached .catch on
        // wdPromise prevents unhandledrejection on late settle (the wedged
        // decoder may never reject its native promise; we orphan it).
        var wdReject = null;
        var wdPromise = new Promise(function(_resolve, reject){ wdReject = reject; });
        wdPromise.catch(function(){});
        var wdIntervalId = setInterval(function(){
            if (stallTriggered) return;
            if (samplesFed === 0) return;
            // ⚠️ Once every sample is fed, silence is NORMAL: decoder.flush()
            // drains the reorder buffer and emits nothing until it finishes.
            // On a long-GOP clip that drain legitimately exceeds
            // MID_STREAM_DEADLINE_MS (10 s), so this watchdog fired FIRST and
            // reported «no-output-deadline:mid-stream» for a decoder that was
            // merely finishing — the flush could never reach its own 15 s
            // budget. Measured 2026-08-20 on a 720p/10.8 s clip with ONE
            // keyframe: same file failed at 37 s on one run and SUCCEEDED at
            // 141 s on the next, purely on scheduling luck. That is the shape
            // of report #1445 («ничего не работает», decoded=116 queue=0
            // fed=120 — i.e. everything fed, nothing left to do but drain).
            // One phase, one owner: feeding is policed here, draining by
            // FLUSH_DEADLINE_MS.
            if (feedingDone) return;
            var idle = silenceMs();
            var kind = null;
            if (decoderFrameCount === 0 && idle > FIRST_FRAME_DEADLINE_MS) kind = 'first-frame';
            else if (decoderFrameCount > 0 && idle > MID_STREAM_DEADLINE_MS) {
                // The interval runs while the feed loop sits in `await onFrame`:
                // that is the starved case this watchdog used to mistake for a
                // stall (source=interval on 101 of 156 phone stalls) — and, with
                // frames held, the decoder out of pictures (heldByUs above).
                if (decoderHasWork() && !heldByUs()) kind = 'mid-stream';
                else if (idle > CONSUMER_SLOW_MS) noteConsumerSlow(idle);
            }
            if (!kind) return;
            stallTriggered = true;
            try {
                if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: slugTag,
                        action: 'webcodecs_decoder_stalled',
                        stage: 'extract',
                        hint: hint || 'default',
                        hint_idx: hintIdx,
                        codec: meta && meta.codec,
                        deadline_kind: kind,
                        decoded_so_far: decoderFrameCount,
                        queue_size: decoder.decodeQueueSize,
                        samples_fed: samplesFed,
                        idle_ms: idle,
                        held: decodedFrames.length,
                        source: 'interval'
                    });
                }
            } catch (_) {}
            if (wdReject) {
                wdReject(new Error('webcodecs-fallback:no-output-deadline:' + kind +
                    ':hint=' + (hint || 'default') +
                    ':decoded=' + decoderFrameCount +
                    ':queue=' + decoder.decodeQueueSize +
                    ':fed=' + samplesFed +
                    ':source=interval'));
                wdReject = null;
            }
        }, 2000);

        try {
            await Promise.race([wdPromise, (async function _extractWork(){
            // ── Feed loop, driven by the parser ───────────────────────────
            // mp4box is a streaming parser: appendBuffer() returns the byte
            // offset it wants NEXT. The only correct feeding strategy is to
            // serve that request — forwards when it skips a box it doesn't
            // need yet, and BACKWARDS when it returns to bytes it skipped.
            //
            // What was here before modelled the file itself: a head scan
            // (capped at 32 MB), a tail scan (last 16 MB) and a body scan,
            // with the head phase ending the moment `onReady` fired. For a
            // moov-at-END file — which is how every iPhone writes .MOV, and
            // what ffmpeg produces without +faststart — that is precisely the
            // wrong moment to stop. Measured on a 21.9 MB / 1992-frame file:
            //
            //   append [0..4MB)      → next=22949046   (skip the mdat)
            //   append [22949046..)  → onReady, 350 samples from what it holds
            //                        → next=4194304   ← "give me the body"
            //   loop exits on infoReady; nobody serves that request
            //   result: 350 of 1992 frames, reported as SUCCESS in 1.3 s
            //
            // Files larger than the old 32 MB head cap escaped by accident:
            // they fell into the tail phase, which re-fed the body from 0.
            // That is why the same user's 17.8 MB and 25.7 MB clips lost 80%
            // of their frames while a 34.5 MB one came out whole.
            //
            // Serving the parser instead of guessing the layout also drops
            // both magic constants: measured across moov-front / moov-end /
            // fragmented / 44 MB / truncated inputs, this reads exactly
            // file.size bytes with zero duplicate samples.
            var want = 0;          // byte offset the parser is asking for
            var maxFedEnd = 0;     // highest offset handed over so far
            var lastWant = -1, sameWantRuns = 0;
            // Generous, but finite: a malformed file must not be able to keep
            // the loop asking forever. Four passes over the file is far more
            // than any real layout needs (measured worst case: one).
            var iterCap = Math.ceil(file.size / CHUNK) * 4 + 64;
            var iters = 0;
            while (!demuxError && !rangeReached) {
                checkAbort();
                checkStall();
                if (++iters > iterCap) break;
                if (want >= file.size) {
                    if (maxFedEnd >= file.size) break;   // everything delivered
                    want = maxFedEnd;                    // nothing out there — continue sequentially
                }
                var end = Math.min(want + CHUNK, file.size);
                var buf = await _slice(want, end);
                buf.fileStart = want;
                var next = demuxFile.appendBuffer(buf);
                if (end > maxFedEnd) maxFedEnd = end;
                var nextWant = (typeof next === 'number' && next >= 0) ? next : end;
                // onReady just jumped to the keyframe before a range window:
                // serve the bytes of THAT sample next, not the parser's linear
                // continuation (see the seek in onReady).
                if (pendingSeekOffset != null) {
                    nextWant = pendingSeekOffset;
                    if (nextWant < maxFedEnd && nextWant >= want && nextWant < end) nextWant = end;
                    pendingSeekOffset = null;
                }
                // A parser repeating the same request without consuming it is
                // not progressing; step past it rather than spin.
                if (nextWant === lastWant) {
                    if (++sameWantRuns >= 2) { nextWant = end; sameWantRuns = 0; }
                } else {
                    sameWantRuns = 0;
                    lastWant = nextWant;
                }
                want = nextWant;
                if (pendingSamples.length) await pumpDecoder();
                if (maxFedEnd >= file.size && want >= file.size) break;
            }
            if (!infoReady && !demuxError) throw new Error('webcodecs-fallback:no-moov');
            try { demuxFile.flush(); } catch(_){}
            if (pendingSamples.length) await pumpDecoder();
            if (demuxError) throw demuxError;
            // Everything the demuxer had is now inside the decoder. From here
            // on only the flush can produce output, and it has its own
            // deadline — stand the feeding watchdog down.
            feedingDone = true;

            // Final flush — drain anything still in the decoder pipeline.
            // Guarded by FLUSH_DEADLINE_MS of decoder silence because a wedged
            // HW decoder's flush() can hang indefinitely (no error, no
            // resolution). We track the original promise so we can attach
            // orphan-catch hygiene if the deadline fires (a wedged decoder
            // can't be reclaimed from JS — runP.catch(()=>{}) swallows the
            // unhandledrejection that fires if/when the late settle arrives).
            if (!rangeReached) {
                var flushP = (function(){
                    try { return decoder.flush(); } catch(_) { return Promise.resolve(); }
                })();
                pendingDecoderFlush = flushP;
                flushP.catch(function(){});   // pre-attach so a late reject doesn't leak
                var flushTimedOut = false;
                var flushDone = false;
                flushP.then(function(){ flushDone = true; }, function(){ flushDone = true; });
                /* Keep CONSUMING output while the flush runs.
                 *
                 * The flush used to be awaited with nobody draining
                 * `decodedFrames`: every frame the decoder produced during the
                 * drain sat there unclosed. A hardware decoder renders into a
                 * fixed pool of output pictures, and a VideoFrame that is not
                 * closed keeps its picture — so the decoder ran out of places
                 * to put the last frames, stopped, and flush() never resolved.
                 * The watchdog below then declared a wedge that we had caused
                 * ourselves. (Headless Chromium decodes in software with no
                 * such pool, which is why the benches that ran there never
                 * saw it.)
                 *
                 * The deadline now measures the DECODER'S silence while there
                 * is nothing left to consume — not wall time since the flush
                 * started. A consumer that takes seconds per frame (an
                 * encoder bridge, a model) is work, not a wedge. */
                var waitFrom = Date.now();
                while (!flushDone) {
                    checkAbort();
                    var stepF = await drainOne();
                    if (stepF === 'past-range') { rangeReached = true; break; }
                    if (stepF) { waitFrom = Date.now(); continue; }
                    if (flushDone) break;
                    // Nothing to consume: sleep until the decoder hands over a
                    // frame or the flush settles — not a spin. The 250 ms wake
                    // only re-reads the deadline and the abort signal.
                    await new Promise(function (wake) {
                        outputWaiter = wake;
                        flushP.then(wake, wake);
                        setTimeout(wake, 250);
                    });
                    outputWaiter = null;
                    if (lastOutputAt > waitFrom) waitFrom = lastOutputAt;
                    if (!flushDone && !decodedFrames.length && Date.now() - waitFrom > FLUSH_DEADLINE_MS) { flushTimedOut = true; break; }
                }
                if (flushTimedOut) {
                    // The flush promise is now orphaned. .catch was pre-attached
                    // so the runtime won't fire unhandledrejection.
                    stallTriggered = true;
                    try {
                        if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                            window.__ae('feature_use', {
                                slug: slugTag,
                                action: 'webcodecs_decoder_stalled',
                                stage: 'extract',
                                hint: hint || 'default',
                                hint_idx: hintIdx,
                                codec: meta && meta.codec,
                                src_w: meta && meta.width,
                                src_h: meta && meta.height,
                                deadline_kind: 'flush',
                                decoded_so_far: decoderFrameCount
                            });
                        }
                    } catch (_) {}
                    throw new Error('webcodecs-fallback:flush-stalled:hint=' +
                        (hint || 'default') + ':decoded=' + decoderFrameCount);
                }
                while (true) {
                    var step3 = await drainOne();
                    if (step3 === 'past-range' || !step3) break;
                }
            }
            if (decodeError) throw decoderErrorFor(decodeError, decoderFrameCount);

            // ── Completeness check ────────────────────────────────────────
            // The bug this guards is not "the demuxer stopped early" — that
            // one is fixed above. It is the CLASS: a run that delivers part of
            // the video and reports success. That shape hid an 80% frame loss
            // for weeks (17.7% of WebCodecs runs came back short against 3.2%
            // on the FFmpeg lane) because nothing ever compared what the
            // container declares with what we produced.
            //
            // The container tells us how many samples exist, so check it. The
            // invariant holds for every mode at once: everything is either
            // decoded or deliberately skipped at the demuxer (keyframes mode).
            // Range mode stops on purpose, and an abort is the user's doing —
            // both are excluded.
            //
            // A short read raises the typed fallback error, so the caller
            // re-runs on FFmpeg and the user gets a COMPLETE result instead of
            // a quiet 18%. Silence is the one outcome ruled out.
            if (!rangeReached && !(abort && abort.aborted) && meta && meta.nbSamples > 0) {
                var handled = decoderFrameCount + samplesSkipped + seekSkipped;
                if (handled < meta.nbSamples * 0.9) {
                    try {
                        if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                            window.__ae('feature_use', {
                                slug: slugTag, action: 'webcodecs_short_read', stage: 'extract',
                                got: handled, expected: meta.nbSamples,
                                pct: Math.round(handled / meta.nbSamples * 100),
                                mode: opts.mode || 'all', codec: meta.codec,
                                size: file.size, hint: hint || 'default'
                            });
                        }
                    } catch (_) {}
                    throw new Error('webcodecs-fallback:short-read:' + handled + '/' + meta.nbSamples);
                }
            }
            })()]);
        } finally {
            // Watchdog interval — clear before any teardown to stop new
            // stallTriggered side-effects firing while we're closing things.
            if (wdIntervalId) { try { clearInterval(wdIntervalId); } catch(_){} wdIntervalId = null; }
            wdReject = null;
            // ETALON-J: null mp4box callbacks so any late onSamples/onError
            // doesn't keep allocating pendingSamples buffers (observed minor
            // leak on 156 MB AVI when a wedged-codec stall threw and mp4box
            // kept firing for seconds until GC reclaimed the closure).
            try {
                demuxFile.onError = null;
                demuxFile.onSamples = null;
                demuxFile.onReady = null;
            } catch (_) {}
            try { if (typeof demuxFile.stop === 'function') demuxFile.stop(); } catch(_) {}
            try { decoder.close(); } catch(_){}
            // Release any frames we never got to render (cancellation, decode error)
            for (var fi = 0; fi < decodedFrames.length; fi++) {
                try { decodedFrames[fi].close(); } catch(_){}
            }
            decodedFrames.length = 0;
        }

        onProgress(1.0);
        return {
            framesEmitted: emittedCount,
            framesDecoded: frameIndex,
            decoderHardware: decoderHardware,
            sec: (performance.now() - t0) / 1000,
            codec: meta.codec,
            hint: hint || 'default',
            hint_idx: hintIdx,
            attempts: attemptsTried
        };
    }  // end runAttempt
}

window.WebCodecsFrameExtractor = {
    isSupported: isSupported,
    extract: extract,
    // Exposed for tests
    _probeFile: probeFile,
    _isMp4Family: isMp4Family
};

})();

// Drain stub queue (defer-onerror race defense — same pattern as mp4box-probe)
if (typeof window.__drainSharedFunction === 'function') {
    window.__drainSharedFunction('WebCodecsFrameExtractor');
}
